/** Recent (1.I-1 deliverable 23): the canon pages opened last, in the sidebar and on home. */
import { Button, Skeleton, TYPE_KEYS, TypeGlyph, type TypeKey } from '@codex/ui';
import { docHref } from '../editor-services/adapter.js';
import { useOpenDoc } from './open-doc.js';
import { PinDocButton, usePinActions } from './pins.js';
import { useRecent } from './queries.js';

export const RECENT_EMPTY = 'Pages you open appear here.';
export const SIDEBAR_RECENT_LIMIT = 8;
export const HOME_RECENT_LIMIT = 20;

function glyphKey(key: string | null): TypeKey {
  return key !== null && (TYPE_KEYS as readonly string[]).includes(key) ? (key as TypeKey) : 'note';
}

/** The list itself: a skeleton, an error with "Try again", the empty copy, or the rows. */
export function RecentList({ limit, testId }: { limit: number; testId?: string }) {
  const recent = useRecent(limit);
  const open = useOpenDoc();
  const actions = usePinActions();
  if (recent.isPending) return <Skeleton lines={3} variant="rows" label="Loading recent pages" />;
  if (recent.isError) {
    return (
      <div className="cx-shell-list-error">
        <p role="alert">Couldn't load this list.</p>
        <Button size="sm" onClick={() => void recent.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  const docs = recent.data.filter((doc) => doc.state === 'live' && doc.title !== null);
  if (docs.length === 0) {
    return (
      <ul className="cx-nav-section__list" data-testid={testId}>
        <li className="cx-shell-empty">{RECENT_EMPTY}</li>
      </ul>
    );
  }
  return (
    <ul className="cx-nav-section__list" data-testid={testId}>
      {docs.map((doc) => (
        <li key={doc.id} className="cx-recent-row cx-nav-row" data-doc-id={doc.id}>
          <TypeGlyph type={glyphKey(doc.typeKey)} />
          <a
            className="cx-nav-row__label cx-shell-link"
            href={docHref(doc.id)}
            title={doc.title as string}
            onClick={(event) => open(doc.id, event)}
          >
            {doc.title}
          </a>
          <PinDocButton docId={doc.id} actions={actions} />
        </li>
      ))}
    </ul>
  );
}

export function RecentSection() {
  return (
    <section className="cx-nav-section" aria-label="Recent">
      <div className="cx-nav-section__head">
        <h2 className="cx-nav-section__title">Recent</h2>
      </div>
      <RecentList limit={SIDEBAR_RECENT_LIMIT} testId="shell.sidebar.recent" />
    </section>
  );
}
